import { useCallback } from 'react';
import { omit } from 'lodash';
import { createHook, createStore, defaults } from 'react-sweet-state';

import { getStateFromUrl, setUrlFromState } from '../utilities/PageStateUrlUtilities';
import { kabobToPascalCase } from '../utilities/StringUtilities';

// Integrate react-sweet-state with the redux dev tools.
defaults.devtools = true;

export function addPageIdPrefix(key, pageId) {
    if (!key) return undefined;
    return `${kabobToPascalCase(pageId)}-${key}`;
}

export function stripPageIdPrefix(key, pageId) {
    const pageKeyPrefix = `${kabobToPascalCase(pageId)}-`;
    return key.replace(pageKeyPrefix, '');
}

const PageStateStore = createStore({
    name: 'PageState',

    initialState: {
        values: {},
    },

    actions: {
        initialize: (pageId) => {
            return ({ getState, setState }) => {
                setState({
                    pageId,
                    values: getStateFromUrl() || {},
                });

                setUrlFromState(getState().values);
            };
        },

        setValue: (key, value) => {
            return ({ getState, setState }) => {
                const state = getState();

                setState({
                    values: {
                        ...state.values,
                        [stripPageIdPrefix(key, state.pageId)]: value,
                    },
                });

                setUrlFromState(getState().values);
            };
        },

        clearValue: (key) => {
            return ({ getState, setState }) => {
                const state = getState();

                setState({
                    values: omit(state.values, stripPageIdPrefix(key, state.pageId)),
                });

                setUrlFromState(getState().values);
            };
        },
    },
});

export const usePageState = createHook(PageStateStore);

export const usePageStateValues = createHook(PageStateStore, {
    selector: (state) => state.values,
});

export const usePageStateValue = createHook(PageStateStore, {
    selector: (state, { key, defaultValue }) => {
        const realKey = stripPageIdPrefix(key, state.pageId);
        let value = state.values?.[realKey];
        if (value === undefined && realKey.includes('_')) {
            // After upgrading to Node 22 + react-scripts 5, terser keep_fnames:true
            // produces double names:  SiteListPanel_SiteListPanel-xxx
            // Legacy data uses single name:  SiteListPanel-xxx
            // Drop the first "_*" segment to read old records
            const legacyKey = realKey.replace(/^[^_]+_/, '');
            value = state.values?.[legacyKey];
        }
        return value !== undefined ? value : defaultValue;
    },
});

export function usePageStateValueWithDefault(key, defaultValue) {
    const [value, pageStateActions] = usePageStateValue({ key, defaultValue });

    const setValue = useCallback(
        (newValue) => {
            if (newValue === defaultValue) {
                // The new value is the same as the default value, so clear the page state to keep the URL clean.
                pageStateActions.clearValue(key);
            } else {
                pageStateActions.setValue(key, newValue);
            }
        },
        [key, defaultValue, pageStateActions]
    );

    return [value, setValue];
}
